import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Upload } from 'lucide-react';
import { ref as dbRef, set, remove, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const storage = getStorage();

export default function MembersManager({ db, showToast, members }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', role: '', bio: '', image: '' });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // State for modal visibility
  const [memberToDeleteId, setMemberToDeleteId] = useState(null); // State for storing ID to delete
  const fileInputRef = useRef(null);

  const roles = ['Vocalist', 'Guitarist', 'Bassist', 'Drummer', 'Keyboardist', 'Lead Singer', 'Backup Vocalist'];

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const storageReference = storageRef(storage, `members/${Date.now()}-${file.name}`);
      await uploadBytes(storageReference, file);
      const downloadUrl = await getDownloadURL(storageReference);
      setFormData({ ...formData, image: downloadUrl });
      showToast('Image uploaded successfully!');
    } catch (err) {
      showToast('Error uploading image', 'error');
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.role) {
      showToast('Please fill all required fields', 'error');
      return;
    }

    try {
      if (editingId) {
        // Edit Member Logic: Uses the stored editingId (Firebase key) to update the existing record
        await set(dbRef(db, `content/members/${editingId}`), {
          name: formData.name,
          role: formData.role,
          bio: formData.bio,
          image: formData.image
        });
        showToast('Member updated successfully!');
      } else {
        // Add Member Logic: Pushes a new record
        const newMemberRef = push(dbRef(db, 'content/members'));
        await set(newMemberRef, {
          name: formData.name,
          role: formData.role,
          bio: formData.bio,
          image: formData.image
        });
        showToast('Member added successfully!');
      }
      setFormData({ name: '', role: '', bio: '', image: '' });
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      showToast('Error saving member', 'error');
    }
  };

  const handleEdit = (member) => {
    // Edit Initiation: Sets formData with current member details and sets editingId (Firebase key)
    setFormData({ ...member });
    setEditingId(member.id); 
    setShowForm(true);
  };

  const promptDelete = (id) => {
    // Show confirmation modal and store the ID of the member to be deleted
    setMemberToDeleteId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    // Adding console log for user debugging
    console.log('Attempting to delete member ID:', memberToDeleteId);

    if (!memberToDeleteId) {
      const errorMsg = 'Error: Member ID missing for deletion.';
      console.error(errorMsg);
      showToast(errorMsg, 'error');
      cancelDelete();
      return;
    }

    try {
      // Delete Member Logic: Uses the robust Firebase ID (memberToDeleteId) to remove the record
      await remove(dbRef(db, `content/members/${memberToDeleteId}`));
      showToast('Member deleted successfully!', 'success');
      console.log('Member deleted:', memberToDeleteId);
    } catch (err) {
      const errorMsg = err.message || 'Error deleting member. Please try again.';
      console.error('Delete error:', err);
      showToast(errorMsg, 'error');
    } finally {
      // Close the modal and reset ID
      cancelDelete();
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setMemberToDeleteId(null);
  };

  // Find the member object currently marked for deletion to display the name in the modal
  const memberToDelete = members.find(m => m.id === memberToDeleteId);
  const memberName = memberToDelete ? memberToDelete.name : 'this member';


  return (
    <div>
      {/* Confirmation Modal (Replaces window.confirm) */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-[#16161b] rounded-xl shadow-2xl p-6 max-w-sm w-full"
              initial={{ scale: 0.9, y: -20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: -20 }}
            >
              <h2 className="text-xl font-semibold text-red-600 mb-4">Confirm Deletion</h2>
              <p className="text-[#c9c9d4] mb-6">
                Are you sure you want to permanently delete **{memberName}**? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-[#33333d] hover:bg-[#26262d] text-[#c9c9d4] rounded-lg font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({ name: '', role: '', bio: '', image: '' });
        }}
        className="mb-8 flex items-center gap-2 px-6 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition"
      >
        <Plus size={20} /> Add Member
      </motion.button>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#16161b] rounded-lg shadow-lg p-6 mb-8"
        >
          {/* Dynamically set the form title based on editing state */}
          <h2 className="text-2xl font-semibold mb-6 text-[#bd9f67]">
            {editingId ? 'Edit Member' : 'Add New Member'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">Member Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Winner Asidor"
                className="w-full px-4 py-2 border border-[#33333d] rounded-lg text-[#c9c9d4] focus:outline-none focus:ring-2 focus:ring-[#bd9f67]"
              />
            </div>

            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">Role *</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 border border-[#33333d] rounded-lg text-[#c9c9d4] focus:outline-none focus:ring-2 focus:ring-[#bd9f67]"
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about this member..."
                rows="3"
                className="w-full px-4 py-2 border border-[#33333d] rounded-lg text-[#c9c9d4] focus:outline-none focus:ring-2 focus:ring-[#bd9f67]"
              />
            </div>

            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">Upload Photo</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition disabled:opacity-50"
                >
                  <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Photo'}
                </button>
              </div>
              {formData.image && (
                <div className="mt-3">
                  <img src={formData.image} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition"
              >
                {editingId ? 'Update Member' : 'Add Member'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ name: '', role: '', bio: '', image: '' });
                }}
                className="flex-1 px-4 py-2 bg-[#33333d] hover:bg-[#26262d] text-[#c9c9d4] rounded-lg font-medium transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Members List */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {members.map((member) => (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#243137] rounded-lg shadow-lg overflow-hidden border border-[#bd9f67]/20 hover:border-[#bd9f67]/40 hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] transition"
          >
            {member.image && (
              <img src={member.image} alt={member.name} className="w-full h-40 object-cover border-b border-[#bd9f67]/30" />
            )}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-[#f9f9f9] mb-1">{member.name}</h3>
              <p className="text-sm text-[#bd9f67] font-medium mb-2">{member.role}</p>
              <p className="text-sm text-[#a6a6b5] mb-4 line-clamp-2">{member.bio}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(member)} // Calls handleEdit with the member object (including ID)
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-[#f9f9f9] rounded-lg transition text-sm"
                >
                  <Edit2 size={14} /> Edit
                </button>
                <button
                  onClick={() => promptDelete(member.id)} // Calls promptDelete to show the modal
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white/10 hover:bg-red-600/80 text-red-400 hover:text-white rounded-lg transition text-sm"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {members.length === 0 && (
        <p className="text-center text-[#7d7d8a] py-8">No members yet. Add one to get started!</p>
      )}
    </div>
  );
}